import { PageContainer } from '../../../components/PageContainer';
import { CameraCard } from '../../../components/CameraCard';
import styled from 'styled-components';
import { useGetCamerasQuery, useGetCombinedDataUsageQuery } from '../../cameras/api/camerasApi';
import { useGetUserDataQuery } from '../../auth/api/authApi';
import { useSelector } from 'react-redux';
import { authSelector } from '../../auth/state/authSlice';
import { FullScreenSpinner } from '../../../components/Spinner/Spinner';
import { useUpdateCameraName } from '../../cameras/hooks/useUpdateCameraName';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { Camera } from '../../cameras/api/types';
import { usePauseSubscriptionModal } from '../../subscription/hooks/usePauseSubscriptionModal';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';

export const Dashboard = () => {
  const { token } = useSelector(authSelector);
  const [editCamera, setEditCamera] = useState<Camera | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedStripeId, setSelectedStripeId] = useState<string>('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Check if we should auto-refresh based on URL parameter
  const shouldRefresh = searchParams.get('refresh') === 'true';

  useGetUserDataQuery({ token: token || '' }, { skip: !token });
  
  const { data, isLoading: loadingCameras, refetch } = useGetCamerasQuery(
    undefined,
    {
      skip: !token,
      refetchOnMountOrArgChange: true
    }
  );

  const { data: combinedUsage } = useGetCombinedDataUsageQuery();

  const runOutWarning = useMemo(() => {
    if (!combinedUsage || !data) return null;
    const planTotalGB = data.overallSummary.totalProductGB;
    if (!planTotalGB || combinedUsage.daily.length === 0) return null;
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    if (combinedUsage.month !== currentMonth) return null;
    const last = combinedUsage.daily[combinedUsage.daily.length - 1];
    const latestDay = new Date(last.date + 'T00:00:00').getDate();
    if (latestDay === 0 || last.cumulativeGB === 0) return null;
    const [y, m] = combinedUsage.month.split('-').map(Number);
    const daysInMonth = new Date(y, m, 0).getDate();
    const dailyRate = last.cumulativeGB / latestDay;
    if (dailyRate * daysInMonth > planTotalGB) {
      const runOutDay = Math.ceil(planTotalGB / dailyRate);
      const runOutDate = new Date(y, m - 1, Math.min(runOutDay, daysInMonth));
      return runOutDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
    }
    return null;
  }, [combinedUsage, data]);

  const { NewNameModal } = useUpdateCameraName({ camera: editCamera, setEditCamera });

  // Improved refresh function
  const handleRefresh = useCallback(async () => {
    // Set refreshing state immediately
    setIsRefreshing(true);

    try {
      // Force a refetch of the cameras data without passing invalid arguments
      const refreshResult = await refetch();
      
      if (!refreshResult.data) {
        // If no data, wait and try once more
        await new Promise(resolve => setTimeout(resolve, 1000));
        await refetch(); // Remove the argument
      }
      
      // Add a small delay to ensure UI updates
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setIsRefreshing(false);
      
      // Clear the refresh parameter from URL after we've done the refresh
      if (shouldRefresh) {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [refetch, navigate, shouldRefresh]);

  // Trigger refresh automatically if needed
  useEffect(() => {
    if (shouldRefresh && !isRefreshing) {
      handleRefresh();
    }
  }, [shouldRefresh, handleRefresh, isRefreshing]);

  const { PauseSubscriptionModal } = usePauseSubscriptionModal({
    stripeId: selectedStripeId,
    isOpen,
    setIsOpen,
    onPauseSuccess: handleRefresh
  });

  const onOpenPauseModal = (stripeId: string) => {
    setSelectedStripeId(stripeId);
    setIsOpen(true);
  };

  const isTerminated = (camera: Camera) =>
    camera.SimStatus === 'terminated' || camera.SubStatus === 'terminated';

  const { activeCameras, terminatedCameras } = useMemo(() => {
    const cameras = data?.cameras ?? [];
    return {
      activeCameras: cameras.filter((c) => !isTerminated(c)),
      terminatedCameras: cameras.filter(isTerminated)
    };
  }, [data]);

  // If the customer only has terminated SIMs, show them by default.
  const onlyTerminated = activeCameras.length === 0 && terminatedCameras.length > 0;
  const [showTerminated, setShowTerminated] = useState(false);
  const terminatedVisible = onlyTerminated || showTerminated;

  const hasCameras = activeCameras.length > 0 || terminatedCameras.length > 0;
  const percentageLeft = data?.overallSummary?.overallPercentageLeft ?? 0;

  return (
    <PageContainer title="Streaming Plans">
      <Wrapper>
        {loadingCameras || isRefreshing ? (
          <FullScreenSpinner />
        ) : !hasCameras ? (
          <DashboardSection title="My cameras">
            <EmptyStateCard>
              <EmptyStateTitle>No cameras yet</EmptyStateTitle>
              <EmptyStateText>
                Once you activate your Farmstream SIM, your cameras and data usage will show up
                here.
              </EmptyStateText>
              <ActivateCta to="/activate">Activate your SIM &rarr;</ActivateCta>
            </EmptyStateCard>
          </DashboardSection>
        ) : (
          <DashboardSection title="My cameras">
            <StyledDataUsageLink to="/data-usage">
              <TopDataUsage>
                <DataUsedText>
                  {percentageLeft.toFixed(1)}% of combined plan remaining
                </DataUsedText>
                <Bar
                  role="progressbar"
                  aria-valuenow={percentageLeft}
                  aria-valuemin={0}
                  aria-valuemax={100}>
                  <InnerBar percentage={percentageLeft} />
                </Bar>
                <ResetText>Plans reset on the 1st of each month</ResetText>
                {runOutWarning && (
                  <DashboardWarning>
                    ⚠️ At current rate, data runs out on {runOutWarning}
                  </DashboardWarning>
                )}
                <ViewDetailText>View combined data usage &rarr;</ViewDetailText>
              </TopDataUsage>
            </StyledDataUsageLink>
            <StyledGrid>
              {(onlyTerminated ? terminatedCameras : activeCameras).map((camera) => (
                <CameraCard
                  key={camera.SIM_ICCID}
                  camera={camera}
                  onEditCamera={setEditCamera}
                  onOpenPauseModal={onOpenPauseModal}
                  onRefreshData={handleRefresh} // Pass the improved refresh function
                />
              ))}
            </StyledGrid>
            {!onlyTerminated && terminatedCameras.length > 0 && (
              <TerminatedSection>
                <TerminatedDivider
                  type="button"
                  onClick={() => setShowTerminated((v) => !v)}
                  aria-expanded={terminatedVisible}>
                  <DividerLine />
                  <DividerLabel>
                    {terminatedVisible ? 'Hide' : 'Show'} terminated SIMs ({terminatedCameras.length})
                  </DividerLabel>
                  <DividerLine />
                </TerminatedDivider>
                {terminatedVisible && (
                  <StyledGrid>
                    {terminatedCameras.map((camera) => (
                      <CameraCard
                        key={camera.SIM_ICCID}
                        camera={camera}
                        onEditCamera={setEditCamera}
                        onOpenPauseModal={onOpenPauseModal}
                        onRefreshData={handleRefresh}
                      />
                    ))}
                  </StyledGrid>
                )}
              </TerminatedSection>
            )}
          </DashboardSection>
        )}
      </Wrapper>
      {PauseSubscriptionModal}
      <NewNameModal />
    </PageContainer>
  );
};

type DashboardSectionProps = {
  title: string;
  children: React.ReactNode;
};

export const DashboardSection = ({ title, children }: DashboardSectionProps) => {
  return (
    <SectionWrapper>
      <SectionTitle>{title}</SectionTitle>
      {children}
    </SectionWrapper>
  );
};

const DataUsedText = styled.p`
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 0.875rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.black};
  letter-spacing: -0.01em;
`;

const Bar = styled.div`
  width: 100%;
  height: 0.75rem;
  border-radius: 0.5rem;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.gray}50, ${({ theme }) => theme.colors.gray}80);
  border: 1px solid ${({ theme }) => theme.colors.gray500}20;
  overflow: hidden;
  box-shadow: 
    inset 0 2px 4px rgba(0, 0, 0, 0.1),
    0 1px 3px rgba(0, 0, 0, 0.05);
  position: relative;
`;

const InnerBar = styled.div<{ percentage: number }>`
  width: ${({ percentage }) => percentage}%;
  height: 100%;
  border-radius: 0.5rem;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.primary}90);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 50%;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, transparent 100%);
    border-radius: 0.5rem 0.5rem 0 0;
  }
`;

const ResetText = styled.p`
  font-size: ${({ theme }) => theme.fontSize.small};
  color: ${({ theme }) => theme.colors.gray900};
  margin-top: 0.875rem;
  text-align: center;
  font-weight: 500;
  opacity: 0.8;
  letter-spacing: 0.01em;
`;

const TopDataUsage = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.white}, ${({ theme }) => theme.colors.gray}15);
  border: 1px solid ${({ theme }) => theme.colors.gray500}20;
  border-radius: 1rem;
  padding: 1.5rem 1.25rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
    border-color: ${({ theme }) => theme.colors.primary}30;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.primary}70);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::before {
    opacity: 1;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SectionWrapper = styled.div``;

const SectionTitle = styled.p`
  margin: 0;
  margin-bottom: 0.6rem;
  font-weight: bold;
`;

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(18rem, 1fr));
  gap: 1rem;
`;

const StyledDataUsageLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: block;
`;

const ViewDetailText = styled.span`
  font-size: ${({ theme }) => theme.fontSize.small};
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
  margin-top: 0.5rem;
  opacity: 0.8;
  transition: opacity 0.2s ease;

  ${TopDataUsage}:hover & {
    opacity: 1;
  }
`;

const DashboardWarning = styled.div`
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffc107;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  font-size: ${({ theme }) => theme.fontSize.small};
  font-weight: 500;
  margin-top: 0.75rem;
  width: 100%;
  text-align: center;
`;

const EmptyStateCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 3rem 1.5rem;
  text-align: center;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.white}, ${({ theme }) => theme.colors.gray}15);
  border: 1px solid ${({ theme }) => theme.colors.gray500}20;
  border-radius: 1rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
`;

const EmptyStateTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.medium};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.black};
`;

const EmptyStateText = styled.p`
  font-size: ${({ theme }) => theme.fontSize.small};
  color: ${({ theme }) => theme.colors.gray900};
  max-width: 28rem;
`;

const ActivateCta = styled(Link)`
  margin-top: 0.5rem;
  padding: 0.625rem 1.5rem;
  border-radius: 0.5rem;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.primary}90);
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fontSize.small};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    opacity: 0.9;
  }
`;

const TerminatedSection = styled.div`
  margin-top: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const TerminatedDivider = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  background: none;
  border: none;
  padding: 0.25rem 0;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.gray900};
  font: inherit;

  &:hover > span {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const DividerLine = styled.span`
  flex: 1;
  height: 1px;
  background: ${({ theme }) => theme.colors.gray500}40;
`;

const DividerLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.small};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray900};
  white-space: nowrap;
  transition: color 0.2s ease;
`;
